import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { NA, formatCurrency, formatDay, formatPercent } from '~/lib/format';
import { allocationsByKind, distributions, fund, holdings, issuerOf, name, taxFor, type Holding, type TaxYear } from '~data';
import { BasisChart } from '~/features/basis-chart';
import { FundFacts } from '~/features/fund-facts';
import { fundSymbolFor } from '~/data/look-through';
import { drillInto, holdingsOpen, openSector, popTo, selectedSymbol, toggleHoldings, toggleSector, trail } from '~/stores/explore-store';

const SectionLabel: Component<{ children: string }> = (props) => (
    <div class='flex h-row items-center border-b bg-muted px-2'>
        <Heading level={2}>{props.children}</Heading>
    </div>
);

// The Holdings label doubles as the handle for the look-through panel. It stays
// a section header rather than becoming a button in the toolbar, because what it
// opens is that section drawn as charts — putting the control anywhere else
// would separate it from the list it is about.
const OpenableSectionLabel: Component<{ children: string; open: boolean; onClick: () => void }> = (props) => (
    <button
        type='button'
        onClick={props.onClick}
        title={props.open ? 'Hide the holdings charts' : 'Show a close chart for each holding'}
        class='flex h-row w-full cursor-default items-center justify-between gap-2 border-b bg-muted px-2 hover:bg-muted-foreground/20'>
        <Heading level={2}>{props.children}</Heading>
        <Text tone='muted'>{props.open ? 'hide charts' : 'charts'}</Text>
    </button>
);

// --- holdings ----------------------------------------------------------------

const HoldingLine: Component<{ holding: Holding }> = (props) => (
    <>
        <div class='flex min-w-0 items-baseline gap-2'>
            {/* Falls back to the symbol we matched it to, for the issuers that
                publish fund holdings by name only. */}
            <Show when={props.holding.symbol ?? fundSymbolFor(props.holding)}>{(ticker) => <Text weight='medium' class='shrink-0'>{ticker()}</Text>}</Show>
            <Text tone='muted' class='truncate'>
                {props.holding.name}
            </Text>
        </div>
        {/* Negative is the cash line on a levered fund — the borrowing. */}
        <Text tone={props.holding.weight < 0 ? 'red' : undefined} class='shrink-0'>
            {formatPercent(props.holding.weight, 1)}
        </Text>
    </>
);

/**
 * A holding, clickable when it is itself a fund we carry.
 *
 * That condition is the whole rule: a company has no page to open because the
 * artefact holds funds, not companies, and a fund we know only by name has
 * nothing to show. Everything else opens, however deep — which is what makes
 * HYLD's nine ETFs explorable rather than a dead end.
 */
const HoldingRow: Component<{ holding: Holding }> = (props) => {
    const inner = () => fundSymbolFor(props.holding);
    return (
        <Show
            when={inner()}
            fallback={
                <div class='flex h-row items-center justify-between gap-2 border-b px-2'>
                    <HoldingLine holding={props.holding} />
                </div>
            }>
            <button
                type='button'
                title={`Open ${inner()}`}
                onClick={() => drillInto(inner()!)}
                class='flex h-row w-full cursor-default items-center justify-between gap-2 border-b px-2 text-left text-sm hover:bg-muted'>
                <HoldingLine holding={props.holding} />
            </button>
        </Show>
    );
};

// --- tax character -----------------------------------------------------------

// What the distribution was actually made of, which for these funds is most of
// the reason to hold one. A 14% yield that is 83% return of capital is not
// income in the year you receive it — it is your own cost base handed back, and
// the tax arrives later as a larger capital gain. Nothing else on this panel
// says that.
//
// Reconciliation, because the components do not obviously add up:
//
//     eligible + capital gains + other + foreign + return of capital
//         − foreign tax withheld  =  total distributed
//
// The income components are gross, and foreign withholding is taken at source
// before the cash reaches you — so on EMAX the parts sum to $1.8769 while the
// fund distributed $1.8320, the $0.0449 gap being tax already paid to another
// country on your behalf (and claimable as a foreign tax credit). Verified to
// hold on every fund that publishes a table.
//
// Shares are therefore taken against what was distributed, with withholding
// carried as a negative line. That makes the column sum to exactly 100% and
// keeps this section agreeing with the Return of capital fact above and the RoC
// column on the screener, which use the same denominator.

const COMPONENTS: { label: string; of: (t: TaxYear) => number }[] = [
    { label: 'Eligible dividends', of: (t) => t.eligibleDividends },
    { label: 'Capital gains', of: (t) => t.capitalGains },
    { label: 'Other income', of: (t) => t.otherIncome },
    { label: 'Foreign income', of: (t) => t.foreignIncome },
    { label: 'Return of capital', of: (t) => t.returnOfCapital },
];

const TaxRow: Component<{ label: string; amount: number; share: number | null; strong?: boolean; tone?: 'muted' | 'red' }> = (props) => (
    <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap'>
        <Text tone={props.strong ? undefined : 'muted'} weight={props.strong ? 'medium' : undefined} class='min-w-0 truncate'>
            {props.label}
        </Text>
        <div class='flex shrink-0 items-baseline gap-3'>
            <Text tone={props.tone} weight={props.strong ? 'medium' : undefined}>
                {formatCurrency(props.amount, 4)}
            </Text>
            {/* Fixed width so the percentages line up into a column rather than
                ragging off the right edge as the amounts change width. */}
            <span class='w-12 text-right'>
                <Text tone='muted'>{formatPercent(props.share, 1)}</Text>
            </span>
        </div>
    </div>
);

const TaxYearBlock: Component<{ year: TaxYear }> = (props) => {
    const total = () => props.year.totalPerUnit;
    const share = (amount: number): number | null => (total() > 0 ? (amount / total()) * 100 : null);

    // Zero components are dropped. A Canadian financials fund pays no foreign
    // income and never will, and four rows of $0.0000 would push the two lines
    // that matter off the fold in a panel this narrow.
    const rows = createMemo(() => COMPONENTS.filter((c) => c.of(props.year) !== 0));

    // Whether the published parts actually tie to the published total. They do
    // on every fund today, but a table that stops reconciling means the issuer
    // changed how it reports, and shares computed off it would be quietly wrong
    // rather than visibly missing.
    const drift = createMemo(() => {
        const parts = COMPONENTS.reduce((sum, c) => sum + c.of(props.year), 0) - props.year.foreignTaxWithheld;
        return parts - total();
    });

    return (
        <>
            <SectionLabel>{`Tax character ${props.year.year}`}</SectionLabel>
            <For each={rows()}>{(c) => <TaxRow label={c.label} amount={c.of(props.year)} share={share(c.of(props.year))} />}</For>

            <Show when={props.year.foreignTaxWithheld !== 0}>
                {/* Negative because it never reached you. Recoverable on a
                    non-registered account as a foreign tax credit; lost in a
                    TFSA, which is the case worth knowing about. */}
                <TaxRow label='Foreign tax withheld' amount={-props.year.foreignTaxWithheld} share={share(-props.year.foreignTaxWithheld)} tone='red' />
            </Show>

            <TaxRow label='Total distributed' amount={total()} share={total() > 0 ? 100 : null} strong />

            <Show when={Math.abs(drift()) > 0.005}>
                <div class='flex h-row items-center border-b px-2'>
                    <Text tone='red' class='truncate'>
                        Components differ from total by {formatCurrency(drift(), 4)}
                    </Text>
                </div>
            </Show>

            <Show when={props.year.returnOfCapital > 0}>
                <div class='flex h-row items-center border-b px-2'>
                    <Text tone='muted' class='truncate'>
                        Return of capital is untaxed on receipt — it lowers cost base
                    </Text>
                </div>
            </Show>
        </>
    );
};

const FundPanel: Component = () => {
    const symbol = () => selectedSymbol();
    const profile = () => fund(symbol());

    const breakdowns = createMemo(() => allocationsByKind(symbol()));
    const positions = createMemo(() => holdings(symbol()));
    // Newest first: what it just paid is the question, not what it paid in 2021.
    const payments = createMemo(() => [...distributions(symbol())].reverse());
    const taxYears = createMemo(() => [...taxFor(symbol())].reverse());

    return (
        <Panel>
            <Toolbar>
                {/* The trail, not just the symbol. Drilling into a fund of funds
                    lands on funds that are often not rows on the screener, so
                    this is the only way back out. A single crumb renders as the
                    plain heading it was before. */}
                <ToolbarSection class='min-w-0'>
                    <For each={trail()}>
                        {(crumb, i) => (
                            <>
                                <Show when={i() > 0}>
                                    <Text tone='muted' class='shrink-0 px-1'>
                                        ›
                                    </Text>
                                </Show>
                                <Show
                                    when={i() < trail().length - 1}
                                    fallback={
                                        <Heading level={2} class={`truncate ${i() === 0 ? 'pl-2' : ''}`}>
                                            {crumb || 'Fund'}
                                        </Heading>
                                    }>
                                    <button type='button' class={`shrink-0 cursor-default rounded px-1 hover:bg-muted ${i() === 0 ? 'ml-1' : ''}`} title={`Back to ${crumb}`} onClick={() => popTo(i())}>
                                        <Text tone='muted'>{crumb}</Text>
                                    </button>
                                </Show>
                            </>
                        )}
                    </For>
                </ToolbarSection>
                <ToolbarSection>
                    <Text tone='muted' class='truncate pr-2'>
                        {profile()?.asOf ? `As at ${profile()!.asOf}` : NA}
                    </Text>
                </ToolbarSection>
            </Toolbar>

            <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
                <div class='flex h-row items-center border-b px-2'>
                    <Text class='truncate'>{name(symbol()) ?? NA}</Text>
                </div>

                {/* Three lines, not one. The unit price alone is the most
                    pessimistic reading of a covered-call ETF and was the only
                    one this panel showed — see basis-chart.tsx. */}
                <BasisChart symbol={symbol()} />

                <SectionLabel>Fund</SectionLabel>
                {/* Shared with the Fund page — see fund-facts.tsx. Two views of
                    one fund listing different things is how they end up
                    disagreeing about what is known. */}
                <FundFacts symbol={symbol()} />

                <Show when={breakdowns().length > 0}>
                    <For each={breakdowns()}>
                        {(breakdown) => (
                            <>
                                <SectionLabel>{breakdown.kind}</SectionLabel>
                                <For each={breakdown.slices}>
                                    {(slice) => (
                                        // Clickable: a slice opens into the holdings
                                        // that make it up. Lit while open, so the
                                        // row says which panel is on screen.
                                        <button
                                            type='button'
                                            title={`Show the holdings in ${slice.name}`}
                                            onClick={() => toggleSector(slice.name)}
                                            class={`flex h-row w-full cursor-default items-center justify-between gap-2 border-b px-2 text-sm ${openSector() === slice.name ? 'bg-muted-foreground/20 border-b-3 border-foreground' : 'hover:bg-muted'}`}>
                                            <span class='truncate'>{slice.name}</span>
                                            <span class='shrink-0'>{formatPercent(slice.weight, 1)}</span>
                                        </button>
                                    )}
                                </For>
                            </>
                        )}
                    </For>
                </Show>

                <Show when={positions().length > 0}>
                    <OpenableSectionLabel open={holdingsOpen()} onClick={toggleHoldings}>
                        Holdings
                    </OpenableSectionLabel>
                    <For each={positions()}>{(holding) => <HoldingRow holding={holding} />}</For>
                </Show>

                {/* Immediately above the payment list: the same money, once as
                    what it was made of and once as when it arrived.

                    Shown even when there is nothing — unlike the sections above,
                    which simply vanish. An absent breakdown is a fund that does
                    not publish one; an absent tax table on a fund yielding 14% is
                    the single thing a Canadian holder most needs and cannot get,
                    and silently dropping the section makes that read as "no tax
                    consequences" rather than "we do not have it yet". */}
                <Show
                    when={taxYears().length > 0}
                    fallback={
                        <>
                            <SectionLabel>Tax character</SectionLabel>
                            <div class='flex h-row items-center border-b px-2'>
                                <Text tone='muted' class='truncate'>
                                    Not published by {issuerOf(symbol()) ?? 'the issuer'}
                                </Text>
                            </div>
                        </>
                    }>
                    <For each={taxYears()}>{(year) => <TaxYearBlock year={year} />}</For>
                </Show>

                <Show when={payments().length > 0}>
                    <SectionLabel>Distributions</SectionLabel>
                    <For each={payments()}>
                        {(d) => (
                            <div class='flex h-row items-center justify-between gap-2 border-b px-2'>
                                <Text tone='muted'>{formatDay(d.exDate)}</Text>
                                <Text tone='green'>{formatCurrency(d.amount, 4)}</Text>
                            </div>
                        )}
                    </For>
                </Show>
            </div>
        </Panel>
    );
};

export default FundPanel;
